import { Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";
import { UseEntityStore } from "../../common/entityStore";
import { keyBy } from "lodash";
import { formatNumber, getLocalizedCurrency, getResource } from "../../common/localization";
import { DefaultValuesDto, useDefaultValues } from "../../common/defaultValues";
import { OrderCalculation, GiftDisplayGroup, GiftDisplayGroupItem } from "../../common/order";
import { TmOrderDto } from "./common";

function getText(key: string, defaultMessage?: string) {
    return getResource("Ui.Order." + key, defaultMessage) as string;
}

export function MailOrderPostageAndGifts({
    useStore,
    info,
    updateOrder,
}: {
    useStore: UseEntityStore<TmOrderDto>;
    info: OrderCalculation;
    updateOrder(): void;
}) {
    const def = useDefaultValues(p => p.clientData);
    const postage = info.tmPostage ?? {};
    return <Grid container item xs={12}>
        {postage.freePostage && <Grid item container xs={12} p={8} borderBottom={1}>
            <Grid item xs={12}>
                <Typography component="span" fontSize={24} color="primary" fontWeight="bold" fontFamily="inherit">{getText("FreePostage")}</Typography> <Typography fontFamily="inherit" component="span" color="black">
                    {getText("FreePostage.From")
                        .replaceAll("{0}", formatNumber(postage.freePostage.fromAmount, def.landCode, true, true))
                        .replaceAll("{1}", getLocalizedCurrency(def.currency))}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                {postage.postage?.all != null ?
                    <>{getText("FreePostage.OtherwiseSingle")
                        .replaceAll("{0}", formatNumber(postage.postage.all, def.landCode, true, true))
                        .replaceAll("{1}", getLocalizedCurrency(def.currency))}</> :
                    postage.postage?.regular != null && postage.postage.parcelShop != null ?
                        <>{getText("FreePostage.OtherwiseDouble")
                            .replaceAll("{0}", formatNumber(postage.postage.regular, def.landCode, true, true))
                            .replaceAll("{1}", getLocalizedCurrency(def.currency))
                            .replaceAll("{2}", formatNumber(postage.postage.parcelShop, def.landCode, true, true))
                            .replaceAll("{3}", getLocalizedCurrency(def.currency))}</> :
                        <></>}
            </Grid>
            <Grid item xs={12}>
                {postage.freePostage != null && postage.freePostage.missingAmount > 0 &&
                    <Typography fontFamily="inherit" component="span" color="red" fontWeight="bold">
                        {getText("FreePostage.MissingAmount")
                            .replaceAll("{0}", formatNumber(postage.freePostage.missingAmount, def.landCode, true, true)
                                .replaceAll("{1}", getLocalizedCurrency(def.currency)))}
                    </Typography>}
            </Grid>
        </Grid>}
        {(info.gifts.giftGroups.length ?? 0) > 0 && <Grid item container xs={12} p={8}>
            <Grid item xs={12}>
                <Typography component="span" fontSize={24} color="primary" fontWeight="bold" fontFamily="inherit">{getText("Gifts")}</Typography>
            </Grid>
            {info.gifts.giftGroups.map((group, i) => <GiftGroupComponent group={group} def={def} useStore={useStore} key={i} updateOrder={updateOrder} />)}
        </Grid>}
    </Grid>;
}

function GiftGroupComponent({ group, def, useStore, updateOrder }: { group: GiftDisplayGroup; def: DefaultValuesDto; useStore: UseEntityStore<TmOrderDto>; updateOrder: () => void; }) {
    function toggleArticle(item: GiftDisplayGroupItem) {
        if (group.missingAmount > 0) {
            return;
        }
        const selectionGroupOtherItemsDict = keyBy(item.selectionGroupOtherItems, p => p.id);
        const autos = useStore.getState().clientData.autos;
        const newAutos = autos.map(p =>
            p.projectMailOrderConditionId === item.moCond.id ? { ...p, quantity: p.quantity > 0 ? 0 : item.moCond.addQuantity, autoSelect: p.quantity === 0 } :
                selectionGroupOtherItemsDict[p.projectMailOrderConditionId!] == null ? p :
                    { ...p, quantity: 0, autoSelect: false });
        useStore.getState().setClientDataProp("autos", newAutos);
        useStore.getState().setClientDataProp("giftsNotSelectedOk", undefined);
        updateOrder();
    }
    const autosDict = useStore(p => keyBy(p.clientData.autos, p => p.projectMailOrderConditionId!));
    function renderItem(item: GiftDisplayGroupItem) {
        const addArticle = item.moCond.addArticle!;
        const detail = autosDict[item.moCond.id];
        if (detail == null) {
            return null;
        }

        const control = <Checkbox checked={detail.quantity > 0} style={{ paddingTop: 0, paddingBottom: 0 }} onChange={p => {
            toggleArticle(item);
        }} />
        return <FormControlLabel label={(item.moCond.addQuantity === 1 ? "" : `${formatNumber(item.moCond.addQuantity, def.landCode, false)}x `) + addArticle.name} control={control} />;
    }
    return <>
        <Grid item xs={12} mt={4} key={group.name + "0"}>
            <Typography component="span" fontSize={16} color="primary" fontFamily="inherit" fontWeight="bold">{group.name}</Typography>
            <Typography component="span" fontFamily="inherit">
                {group.amount > 0 ? <> {getText("Gifts.ForOrderFrom")
                    .replaceAll("{0}", formatNumber(group.amount, def.landCode, true, true))
                    .replaceAll("{1}", getLocalizedCurrency(def.currency))}</> :
                    <> {getText("Gifts.ForEveryOrder")}</>}
            </Typography>
        </Grid>
        <Grid item xs={12}>
            <Typography component="span" color="red" fontFamily="inherit" position="relative" top="-4px">
                {group.missingAmount > 0 && <>{getText("Gifts.MissingAmount")
                    .replaceAll("{0}", formatNumber(group.missingAmount, def.landCode, true, true))
                    .replaceAll("{1}", getLocalizedCurrency(def.currency))}</>}
            </Typography>
        </Grid>
        {group.items.map((p, i) => <Grid item xs={12} key={i}>
            {renderItem(p)}
        </Grid>)}
    </>;
}
